import React, { useContext, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { AuthContext } from '../context/AuthContext';
import { Invoice } from '../models/table-models';
import { Role } from '../utils/Enums/Roles';
import { toastOptionsTopRight } from '../utils/Enums/ToastOptions';
import { offsetWithCreditNote } from '../utils/clients-hooks';
import { isCreditNoteOffsetAvailable } from '../utils/creditNoteOffset';
import { transformDateToString, transformPriceToString } from '../utils/helper-functions';
import { CustomToastContent } from './CustomToastContent';

/**
 * „Прихвани с КИ“ в диалога „Промени“.
 *
 * „Платено“ по кредитирана неплатена фактура би вписало в оборота пари, които не са дошли - известието
 * по неплатена фактура не оставя ред в оборота, срещу който да се нулират. Кога бутонът се показва -
 * виж {@link isCreditNoteOffsetAvailable}.
 */
export const CreditNoteOffsetPanel = ({ invoice, onDone }: { invoice?: Invoice; onDone: () => void }) => {
    const { t } = useTranslation('translation', { keyPrefix: 'invoice' });
    const { hasPermission } = useContext(AuthContext);
    const [showConfirm, setShowConfirm] = useState(false);
    const [comment, setComment] = useState('');
    const [saving, setSaving] = useState(false);

    const document = invoice?.document;
    const offset = invoice?.creditNoteOffset;

    if (offset) {
        return (
            <div className='mb-3 p-3 border rounded bg-light'>
                <strong>
                    {t('offsetInfo', {
                        creditNoteNumber: offset.creditNoteNumber,
                        date: transformDateToString(offset.recordedAt, 'dd/mm/yyyy HH:MM'),
                    })}
                </strong>
            </div>
        );
    }

    // Същите роли, както „Статус“ и „Плащания по фактура“ в диалога - бутонът ги замества.
    if (!hasPermission([Role.FINANCIAL, Role.COORDINATOR]) || !isCreditNoteOffsetAvailable(invoice)) {
        return null;
    }

    const submit = async () => {
        if (!document?.id) return;
        setSaving(true);
        try {
            await offsetWithCreditNote(document.id, comment.trim() || undefined);
            toast.success(<CustomToastContent content={[t('offsetDone')]} />, toastOptionsTopRight);
            setShowConfirm(false);
            onDone();
        } catch {
            // Отказът на сървъра вече е показан от interceptor-а; диалогът остава отворен.
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <div className='mb-3 p-3 border rounded bg-light d-flex justify-content-between align-items-center'>
                <span>{t('offsetHint', { creditNoteNumber: document?.creditNoteNumber })}</span>
                <Button variant='success' size='sm' className='ms-3 text-nowrap' onClick={() => setShowConfirm(true)}>
                    {t('offsetWithCreditNote')}
                </Button>
            </div>

            <Modal show={showConfirm} onHide={() => !saving && setShowConfirm(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{t('offsetConfirmTitle')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>
                        {t('offsetConfirmText', {
                            invoiceNumber: document?.invoiceNumber,
                            creditNoteNumber: document?.creditNoteNumber,
                            amount: `${transformPriceToString(document?.total ?? 0)} ${invoice?.currency ?? ''}`.trim(),
                        })}
                    </p>
                    <p className='text-danger fw-bold'>{t('offsetConfirmNote')}</p>
                    <Form.Group>
                        <Form.Label>{t('offsetComment')}</Form.Label>
                        <Form.Control as='textarea' rows={2} value={comment} maxLength={2000}
                                      onChange={(e) => setComment(e.target.value)} />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer style={{ justifyContent: 'space-between' }}>
                    <Button variant='secondary' disabled={saving} onClick={() => setShowConfirm(false)}>
                        {t('offsetCancel')}
                    </Button>
                    <Button variant='danger' disabled={saving} onClick={submit}>
                        {t('offsetConfirm')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};
